/**
 * Session token store — in-memory only.
 *
 * New architecture (server-backed-auth-refresh):
 *   - The Google refresh token is held in an HttpOnly cookie (`mochia_rt`)
 *     set by the server — JavaScript CANNOT read it, which is the point.
 *   - The access token lives in-memory ONLY. It is never written to
 *     localStorage or sessionStorage.
 *   - On cold load (PWA restart), App.tsx calls postRefresh() to rehydrate
 *     the access token from the server cookie. The cookieKnown flag in
 *     localStorage controls whether to even attempt this (avoiding a wasted
 *     round-trip for users who have never signed in).
 *
 * Why we dropped localStorage persistence (mochia.session.v2):
 *   - The previous hybrid model stored the access token in localStorage to
 *     survive cold loads. With the new cookie-based refresh this is
 *     unnecessary — and stores a short-lived token in a XSS-readable store.
 *   - The new path: cold load → postRefresh → store access_token in memory.
 *
 * Module-init cleanup: any leftover `mochia.session.v2` or `mochia.session.v1`
 * entries from the old architecture are removed on module load. This prevents
 * stale tokens from persisting after the upgrade.
 *
 * The `revoke()` function has been removed. Logout is now handled by
 * `postLogout()` from serverRefresh.ts, which revokes the Google grant
 * server-side and clears the HttpOnly cookie in one round-trip.
 */

const STORAGE_KEY_V2 = 'mochia.session.v2';
const LEGACY_KEY_V1 = 'mochia.session.v1';

interface InMemorySession {
  token: string;
  email: string | null;
  /** Epoch milliseconds when the token expires. Null = no expiry known. */
  expiresAt: number | null;
}

let memory: InMemorySession | null = null;

// Module-init: remove legacy localStorage entries from the old architecture.
// mochia.session.v1 — pre-harden-token-storage shape
// mochia.session.v2 — hybrid in-memory+localStorage from harden-token-storage
// Neither is valid in the new server-backed-refresh architecture.
if (typeof localStorage !== 'undefined') {
  try {
    localStorage.removeItem(LEGACY_KEY_V1);
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
  try {
    localStorage.removeItem(STORAGE_KEY_V2);
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
}

/** Get the current access token from in-memory state. */
export function getToken(): string | null {
  return memory?.token ?? null;
}

/** Get the email associated with the current session (may be null). */
export function getEmail(): string | null {
  return memory?.email ?? null;
}

/** Get the expiry timestamp (ms) of the current token, or null. */
export function getExpiresAt(): number | null {
  return memory?.expiresAt ?? null;
}

/**
 * Stamp an access token into the in-memory session.
 *
 * Named-arg interface prevents accidental arg-order swap (token vs email
 * are both strings; a swap would send PII as a bearer token).
 */
export function setToken(input: {
  token: string;
  email: string | null;
  expiresAt: number | null;
}): void {
  const { token, email, expiresAt } = input;
  memory = { token, email, expiresAt };
}

/** Clear in-memory session state. */
export function clear(): void {
  memory = null;
}
